import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-webapi/commerce-webapi/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "inventory-management"
    }}>{`Inventory Management`}</h1>
    <p>{`Magento Open Source and Adobe Commerce v2.3 include new and expanded features and APIs for inventory management. Inventory Management replaces all core APIs in the Open Source `}<inlineCode parentName="p">{`CatalogInventory`}</inlineCode>{` module and the `}<inlineCode parentName="p">{`ScalableInventory`}</inlineCode>{` module in Commerce. It also provides additional APIs to extend and add functionality.`}</p>
    <p>{`Inventory Management features include:`}</p>
    <ul>
      <li parentName="ul">{`Different configurations for merchants whose inventory originates from a single source and from multiple sources`}</li>
      <li parentName="ul">{`Stocks for tracking available aggregated quantities through assigned sources`}</li>
      <li parentName="ul">{`Concurrent checkout protection`}</li>
      <li parentName="ul">{`Shipment matching algorithms`}</li>
    </ul>
    <p>{`Merchants install Inventory Management as part of v2.3.x and upgrades with the name `}<inlineCode parentName="p">{`magento/inventory-composer-metapackage`}</inlineCode>{`. For details, see `}<a parentName="p" {...{
        "href": "https://experienceleague.adobe.com/docs/commerce-admin/inventory/get-started/install-update.html"
      }}>{`Install and Update Inventory Management`}</a>{`.`}</p>
    <p><strong parentName="p">{`Magento Community Contribution`}</strong>{` – Adobe thanks the many contributors to the `}<a parentName="p" {...{
        "href": "https://github.com/magento/inventory"
      }}>{`Magento Inventory (was MSI) project`}</a>{`, developing these features as part of the Magento Community Engineering program.`}</p>
    <h2 {...{
      "id": "terminology"
    }}>{`Terminology`}</h2>
    <p>{`The following terms are important as you work with Inventory Management APIs:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Sources`}</strong>{` represent physical locations that store and ship available products. These locations can include warehouses, brick-and-mortar stores, distribution centers, and drop shippers. (Any location can be designated as a source for virtual products.)`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Stocks`}</strong>{` map a sales channel (currently limited to websites) to source locations and on-hand inventory. A stock can map to multiple sales channels, but a sales channel can be assigned to only one stock.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Aggregate Salable Quantity`}</strong>{` is the total virtual inventory that can be sold through a sales channel. The amount is calculated across all sources assigned to a stock.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><strong parentName="p">{`Reservations`}</strong>{` track deductions from the salable quantity as customers add products to carts and complete checkout. When an order ships, the reservation clears and deducts the shipped amounts from specific source inventory quantities.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "a-simple-scenario"
    }}>{`A simple scenario`}</h2>
    <p>{`The following diagram illustrates the relationship between source stocks, aggregate stocks, and sales channels:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "656px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "76.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/5530d/inventory-diagram-stock.webp 320w", "/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/0c8fb/inventory-diagram-stock.webp 640w", "/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/0ccf2/inventory-diagram-stock.webp 656w"],
            "sizes": "(max-width: 656px) 100vw, 656px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/dd4a7/inventory-diagram-stock.png 320w", "/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/0f09e/inventory-diagram-stock.png 640w", "/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/5ad6b/inventory-diagram-stock.png 656w"],
            "sizes": "(max-width: 656px) 100vw, 656px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/webapi/static/d4771f062f13343a2f61ec3cb6fc79d9/5ad6b/inventory-diagram-stock.png",
            "alt": "Source and aggregate stock",
            "title": "Source and aggregate stock",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`In this diagram, a bicycle merchant has inventory for a mountain bike in two warehouses and a drop shipper. He has two stocks with configured website sales channels and sources. When a customer shops through the UK website, Magento aggregates bike inventory from the UK warehouse and the drop shipper sources, for a salable quantity of 95. The bike can be shipped from either the warehouse or the drop shipper, but not the NY warehouse. Amazon Marketplace has the same stock, drawing from the same aggregate stock as the UK website.`}</p>
    <h2 {...{
      "id": "important-inventory-management-objects"
    }}>{`Important Inventory Management objects`}</h2>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`Source`}</inlineCode>{` – Defines a physical stock.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`SourceItem`}</inlineCode>{` – A relation object that represents the amount of a specific product at a physical source. We use this entity for updating inventory on each source. Quantities might change as a result of synchronizing with an external Product Information Management (PIM) or Enterprise Resource Planning (ERP) system, or internally as a stock deduction during the checkout process. A `}<inlineCode parentName="p">{`SourceItem`}</inlineCode>{` cannot be used for retrieving data that must be rendered on front-end, because only aggregated data should be used for all validations and UI representation.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`StockItem`}</inlineCode>{` – Also known as Aggregated Virtual Stock. This is read-only data that the re-indexation process generates. Based on a predefined mapping, we define what sources are assigned to the current scope (sales channel) and aggregate quantities from all assigned sources. We also use `}<inlineCode parentName="p">{`StockItem`}</inlineCode>{` to check if a product is in or out of stock.  Making this segregation by Read-Only interface (`}<inlineCode parentName="p">{`StockItem`}</inlineCode>{`) and Write-Only interface (`}<inlineCode parentName="p">{`SourceItem`}</inlineCode>{`), the Inventory architecture achieves Command Query Responsibility Segregation (CQRS). As a result, all `}<inlineCode parentName="p">{`GET`}</inlineCode>{` HTTP requests should use `}<inlineCode parentName="p">{`StockItem`}</inlineCode>{` entity, and all `}<inlineCode parentName="p">{`POST/PUT`}</inlineCode>{` should use `}<inlineCode parentName="p">{`SourceItem`}</inlineCode>{`.`}</p>
      </li>
    </ul>
    <h2 {...{
      "id": "shipping-algorithms"
    }}>{`Shipping algorithms`}</h2>
    <p>{`When merchants are ready to make a partial or full shipment, they select the source or sources from which to send the products. Customers typically want low-cost shipping and a guarantee of safe arrival of products, while the merchant needs to ensure minimal overhead for the inventory storage and shipping costs. Inventory Management includes an algorithm that takes these considerations into account and recommends the best shipping option or options. The application provides an algorithm for Priority, using the source priority per stock, where each source is given a priority in the scope of a specific sales channel, and for Distance, using the locations of sources and shipping destinations. Invenotry Management also supports developer-provided extensions for other algorithms based on criteria such as cheapest shipping and closest GPS location.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      